import {
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Link,
  Typography,
  TextField,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import { useCallback, useState } from "react";
import { Button, SimpleForm, useDataProvider } from "react-admin";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useEffect } from "react";
import _, { debounce } from "lodash";
import { getFixedLocationDropOffBySchoolIds } from "../tools";

interface IAddStudentButton {
  handleAddStudent: (x: IStudent, y: IStudentAddress) => void;
  school: ISchool;
  busScheduleType: string;
  routes: any;
  selectedMarkers: any;
}

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  addStudentButton: {
    width: "100%",
    marginLeft: 0,
    borderRadius: 0,
    padding: 15,
    backgroundColor: "#FFFFFF",
    color: "#6B6B6B",
    border: "1px solid #EAEAEA",
    boxShadow: "unset",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#EAEAEA !important",
    },
  },
  saveStudentButton: {
    width: "100%",
    marginTop: 10,
    marginLeft: 0,
    borderRadius: 0,
    padding: 15,
    backgroundColor: "#E7E7E7",
    color: "#4E4E4E",
    fontWeight: 600,
    boxShadow: "unset",
    "&:hover": {
      backgroundColor: "#D8D8D8 !important",
    },
  },
  closeTextWrapper: {
    float: "right",
    margin: "7px 4px 0 0",
    cursor: "pointer",
  },
  closeText: {
    color: "#474747",
    fontWeight: 600,
    textDecoration: "underline",
  },
  studentSearchInput: {
    marginTop: "8px",
    paddingBottom: "3px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#EAEAEA",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA",
      },
    },
  },
});

export const createAndMergeStudentIds = (students: IStudent[]) => {
  const temp = _.cloneDeep(students);
  const studentIds = temp.map((item: any) => {
    return item ? item.id : item;
  });

  return studentIds;
};

export const mergeStudentIds = (selectedMarkers: any) => {
  let temp: any = [];

  selectedMarkers.forEach((selectedMarker: any) => {
    const studentIds = selectedMarker.students.map((student: IStudent) => {
      return student.id;
    });
    temp = [...temp, ...studentIds];
  });

  return temp;
};

const AddStudentButton = ({
  handleAddStudent,
  school,
  busScheduleType,
  routes,
  selectedMarkers,
}: IAddStudentButton) => {
  const [open, setOpen] = useState<boolean>(false);
  const [students, setStudents] = useState<IStudent[]>([]);
  const [studentAddresses, setStudentAddresses] = useState<IStudentAddress[]>(
    [],
  );
  const [selectedStudent, setSelectedStudent] = useState<IStudent>(null);
  const [selectedStudentAddress, setSelectedStudentAddress] =
    useState<IStudentAddress>(null);
  const [isDisable, setIsDisable] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>("");

  const [isHaveDuplicateStudent, setIsHaveDuplicateStudent] =
    useState<boolean>(false);

  const classes = useStyles();
  const provider = useDataProvider();

  const handleOpenDialogAddStudent = () => {
    setOpen(true);
    setSelectedStudent(null);
    setSelectedStudentAddress(null);
  };

  const handleCloseDialogAddStudent = () => {
    setOpen(false);
  };

  const handleOpenDialogDuplicateStudent = () => {
    setIsHaveDuplicateStudent(true);
  };

  const handleCloseDialogDuplicateStudent = () => {
    setIsHaveDuplicateStudent(false);
  };

  const handleCheckDuplicateStudent = () => {
    const duplicateStudentIdFromRoutes = routes.find((route: any) =>
      route.routeLocations.find((routeLocation: any) =>
        routeLocation.students.find(
          (student: any) => student.id === selectedStudent.id,
        ),
      ),
    );

    const duplicateStudentIdFromSelectedMarker = selectedMarkers.find(
      (selectedMarker: any) =>
        selectedMarker.students &&
        selectedMarker.students.find(
          (student: any) => student.id === selectedStudent.id,
        ),
    );

    if (duplicateStudentIdFromRoutes || duplicateStudentIdFromSelectedMarker) {
      handleOpenDialogDuplicateStudent();
    } else {
      handleAddStudent(selectedStudent, selectedStudentAddress);
      handleCloseDialogAddStudent();
    }
  };

  const getStudents = useCallback(
    debounce((searchString: string) => {
      const filter: any = { SchoolId: school.id, Status: "active" };

      if (busScheduleType === "cca") {
        filter.HasCcaSchedule = true;
      }

      if (
        busScheduleType === "to_school" ||
        busScheduleType === "from_school"
      ) {
        filter.HasRegularSchedule = true;
      }

      provider
        .customGetListWithStaticParams(
          `students?filter=${JSON.stringify(
            filter,
          )}&pageSize=50&search=${searchString}`,
        )
        .then((result: any) => {
          setStudents(result.data as IStudent[]);
        });
    }, 1000),
    [],
  );

  useEffect(() => {
    if (searchValue !== "") {
      getStudents(searchValue);
    }
  }, [searchValue, getStudents]);

  useEffect(() => {
    if (selectedStudent !== null && selectedStudentAddress !== null) {
      setIsDisable(false);
    } else {
      setIsDisable(true);
    }
  }, [selectedStudent, selectedStudentAddress]);

  useEffect(() => {
    if (selectedStudent !== null) {
      studentAddress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudent]);

  const studentAddress = async () => {
    let _studentAddress = await provider.getList("studentAddresses", {
      filter: { StudentId: Number(selectedStudent.id) },
      pagination: {
        page: 1,
        perPage: 1000,
      },
      sort: {
        field: "Id",
        order: "ASC",
      },
    });

    let dataFixLocation: any = [];
    if (busScheduleType === "cca") {
      dataFixLocation = await getFixedLocationDropOffBySchoolIds(
        school.id,
        provider,
      );
    }

    let result = [..._studentAddress.data, ...dataFixLocation];

    setStudentAddresses(result);
  };

  return (
    <>
      <Button
        label="Add Student"
        alignIcon="right"
        className={classes.addStudentButton}
        onClick={handleOpenDialogAddStudent}
      >
        <AddCircleOutlineIcon />
      </Button>
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="xs"
      >
        <DialogTitle id="alert-dialog-title">
          Add The Student
          <Link
            aria-label="close"
            className={classes.closeTextWrapper}
            onClick={handleCloseDialogAddStudent}
          >
            <Typography variant="body2" className={classes.closeText}>
              Cancel
            </Typography>
          </Link>
        </DialogTitle>
        <DialogContent>
          <SimpleForm toolbar={null}>
            <SanitizedGrid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Select Student</Typography>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  className={classes.studentSearchInput}
                  freeSolo
                  disableClearable
                  options={students.map((option: IStudent) => {
                    return `${option.id} - ${option.firstName} ${option.lastName}`;
                  })}
                  onChange={(event, value) => {
                    const splitedValue = value.split(" ");

                    setSelectedStudent(
                      _.find(students, ["id", Number(splitedValue[0])]),
                    );
                  }}
                  onInputChange={(event, newInputValue) => {
                    setSearchValue(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Student"
                      variant="outlined"
                      InputProps={{ ...params.InputProps, type: "search" }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2">
                  Select Student address
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  className={classes.studentSearchInput}
                  freeSolo
                  disableClearable
                  options={studentAddresses.map((option: any) => {
                    let type = "";
                    if (option.isMain === true) {
                      type = "MAIN";
                    } else if (option.isMain === false) {
                      type = "SUB";
                    } else {
                      type = option.name;
                    }
                    return `${option.id} - ${option.blockNumber} ${option.street} ${option.postalCode} - ${type}`;
                  })}
                  onChange={(event, value) => {
                    const splitedValue = value.split(" ");

                    setSelectedStudentAddress(
                      _.find(studentAddresses, ["id", Number(splitedValue[0])]),
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Student Address"
                      variant="outlined"
                      InputProps={{ ...params.InputProps, type: "search" }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  label="Save"
                  className={classes.saveStudentButton}
                  onClick={() => {
                    handleCheckDuplicateStudent();
                  }}
                  disabled={isDisable}
                />
              </Grid>
            </SanitizedGrid>
          </SimpleForm>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isHaveDuplicateStudent}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">
          Info Duplicate Student
        </DialogTitle>
        <DialogContent>
          <DialogContentText color="initial">
            The student that you are trying to add has already been added to
            this wave. do you want to add the student again?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialogDuplicateStudent}
            color="secondary"
            label="Back"
          />
          <Button
            onClick={() => {
              handleAddStudent(selectedStudent, selectedStudentAddress);
              handleCloseDialogDuplicateStudent();
              handleCloseDialogAddStudent();
            }}
            color="primary"
            label="Confirm"
            disabled={isDisable}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddStudentButton;
